import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ListTabType, TableColumn } from '../_models/transport-list.model';
import { TransportListStore } from './_store/transport-list.store';
import { MatTabsModule } from '@angular/material/tabs';
import { TransportListTableComponent } from './transport-list-table/transport-list-table.component';
import { TopBarComponent } from '../_shared/topbar/top-bar.component';
import { TransportAction, TransportModule, TransportSide } from '../_models/transport.model';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { ActivatedRoute } from '@angular/router';
import { TransportListApiService } from './_api/transport-list.api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OfferTransportList } from '../_models/transport-list.model';
import { mapOfferTransportList } from '@/new-transport/_utils/map-offer-transport-list/map-offer-transport-list';

@Component({
	selector: 'app-transport-list',
	standalone: true,
	imports: [MatTabsModule, TransportListTableComponent, TopBarComponent, NavbarModule],
	providers: [TransportListStore],
	templateUrl: './transport-list.component.html',
	styleUrl: './transport-list.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportListComponent {
	// enums
	readonly TransportAction = TransportAction;
	readonly TransportModule = TransportModule;
	readonly TransportSide = TransportSide;

	readonly targetListColumns = computed<TableColumn[]>(() => {
		const base: TableColumn[] = [
			{ field: 'Lp', width: '60px' },
			{ field: 'stopDeparture', width: '160px' },
			{ field: 'status', width: '150px' },
			{ field: 'unique', width: '220px' },
			{ field: 'bc', width: '70px' },
			{ field: 'firstName', width: '150px' },
			{ field: 'lastName', width: '230px' },
			{ field: 'slotCount', width: '60px' },
			{ field: 'seats', width: '150px' },
			{ field: 'comfort', width: '150px' },
			{ field: 'phone', width: '130px' },
			{ field: 'product', width: '300px' },
			{ field: 'note', width: '300px' },
			{ field: 'board', width: '220px' },
			{ field: 'accommodation', width: '150px' },
			{ field: 'otherCities', width: '150px' },
		];
		if (!this.isSegmentMode()) {
			return base;
		}
		return [
			...base,
			{ field: 'isDataEntered', width: '90px' },
			{ field: 'isDepartureCheckIn', width: '90px' },
			{ field: 'isDepartureSent', width: '90px' },
			{ field: 'isReturnCheckIn', width: '90px' },
			{ field: 'isReturnSent', width: '90px' },
			{ field: 'kr', width: '180px' },
			{ field: 'departureTicket', width: '200px' },
			{ field: 'returnTicket', width: '200px' },
			{ field: 'userDocuments', width: '160px' },
			{ field: 'arrivalTime', width: '130px' },
			{ field: 'arrivalAirport', width: '150px' },
			{ field: 'departureFlightNumber', width: '110px' },
			{ field: 'returnDepartureTime', width: '130px' },
			{ field: 'returnAirport', width: '150px' },
			{ field: 'returnFlightNumber', width: '110px' },
		];
	});

	readonly passportListColumns: TableColumn[] = [
		{ field: 'Lp', width: '5%' },
		{ field: 'firstName', width: '10%' },
		{ field: 'lastName', width: '10%' },
		{ field: 'birth', width: '10%' },
		{ field: 'vehicle', width: '10%' },
		{ field: 'product', width: '25%' },
		{ field: 'accommodation', width: '20%' },
		{ field: 'insurer', width: '10%' },
	];

	readonly feeColumns: TableColumn[] = [
		{ field: 'Lp', width: '60px' },
		{ field: 'firstName', width: '150px' },
		{ field: 'lastName', width: '220px' },
		{ field: 'birth', width: '100px' },
		{ field: 'bc', width: '70px' },
		{ field: 'surchargeCost', width: '100px' },
		{ field: 'nights', width: '70px' },
		{ field: 'signature', width: '100px' },
		{ field: 'vehicle', width: '120px' },
		{ field: 'product', width: '300px' },
		{ field: 'board', width: '220px' },
		{ field: 'note', width: '300px' },
		{ field: 'accommodation', width: '150px' },
	];

	readonly tgsColumns: TableColumn[] = [
		{ field: 'Lp', width: '5%' },
		{ field: 'firstName', width: '20%' },
		{ field: 'lastName', width: '20%' },
		{ field: 'bc', width: '10%' },
		{ field: 'product', width: '15%' },
		{ field: 'acceptanceSignature', width: '15%' },
		{ field: 'dedicationSignature', width: '15%' },
	];

	readonly smsColumns: TableColumn[] = [
		{ field: 'checkbox', width: '5%' },
		{ field: 'Lp', width: '5%' },
		{ field: 'smsSent', width: '5%' },
		{ field: 'bc', width: '5%' },
		{ field: 'phone', width: '10%' },
		{ field: 'stopDeparture', width: '10%' },
		{ field: 'address', width: '20%' },
		{ field: 'stopDepartureDate', width: '10%' },
		{ field: 'stopDepartureHour', width: '10%' },
		{ field: 'product', width: '10%' },
		{ field: 'accommodation', width: '10%' },
	];

	readonly ListTabType = ListTabType;

	readonly store = inject(TransportListStore);
	readonly segmentOfferTransportList = signal<OfferTransportList[]>([]);
	readonly isSegmentMode = computed(() => this.route.snapshot.routeConfig?.path === 'segment/:id/list');
	readonly tableData = computed(() => {
		if (this.isSegmentMode()) {
			return this.segmentOfferTransportList();
		}
		return this.store?.offerTransportList() ?? [];
	});
	readonly tableTransportSide = computed(() => {
		if (this.isSegmentMode()) {
			return TransportSide.DEPARTURE;
		}
		return this.store?.transport()?.side ?? TransportSide.DEPARTURE;
	});

	private readonly route = inject(ActivatedRoute);
	private readonly transportListApiService = inject(TransportListApiService);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		if (this.isSegmentMode()) {
			this.fetchSegmentTransportList();
		}
	}

	refreshView(): void {
		if (this.isSegmentMode()) {
			this.fetchSegmentTransportList();
			return;
		}
		this.store?.triggerRefresh();
	}

	private fetchSegmentTransportList(): void {
		const segmentIdParam = this.route.snapshot.paramMap.get('id');
		const segmentId = Number(segmentIdParam);
		if (Number.isNaN(segmentId)) {
			return;
		}
		this.transportListApiService
			.getOfferTransportBySegment(segmentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				this.segmentOfferTransportList.set(mapOfferTransportList(result));
			});
	}
}
